import { ModelService } from 'app/model/services/model.service';
import { Metamodel } from 'app/model/base/model';
import { Trigger } from 'app/model/trigger/trigger';
import { Named } from 'app/model/base/named';
import { Walker } from 'app/model/base/walker';
import { format } from 'app/shared/utils/string.util';

import { resolveResourceType } from 'app/shared/utils/model.concepts.utils';
import { timeout } from 'rxjs/operators';
import { Copier, Factory, IResourceContext, Owned, MODEL_SERVICE_LIST_TIMEOUT } from 'shell-models-lib';
import { GlobalParametersARM } from 'app/model/globalparameters-arm/globalparameters-arm';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';

export class PublisherUtils {
    public static maxResourcesAllowedForPublishing: number = 700;

    public static async getPublishableNameds(
        contextData: IResourceContext,
        options?: {
            includeFactoryWithGlobalParameters_deprecated?: boolean;
            includeFactoryWithCmkSettings_deprecated?: boolean;
            includeFactoryWithGlobalParameters?: boolean;
        }
    ): Promise<Named[]> {
        const modelService = await getServiceInstance<ModelService>(contextData, 'ModelService');
        const {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            includeFactoryWithGlobalParameters_deprecated,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            includeFactoryWithCmkSettings_deprecated,
            includeFactoryWithGlobalParameters
        } = options || {};
        const result: Named[] =
            includeFactoryWithGlobalParameters_deprecated || includeFactoryWithCmkSettings_deprecated
                ? [
                      await this.getFactoryCloneForArmTemplate(contextData, {
                          includeFactoryWithGlobalParameters: includeFactoryWithGlobalParameters_deprecated,
                          includeFactoryWithCmkSettings: includeFactoryWithCmkSettings_deprecated
                      })
                  ]
                : includeFactoryWithGlobalParameters
                ? [await this.createGlobalParametersForArmTemplate(contextData)]
                : [];
        const nameds =
            ((await modelService.list().pipe(timeout(MODEL_SERVICE_LIST_TIMEOUT)).toPromise()) as Named[]) || [];
        return result.concat(nameds);
    }

    public static async getResourcesTopologicalMap(
        context: { contextData: IResourceContext; inlineDataset: boolean },
        inputNameds?: Named[],
        includeFactoryWithGlobalParameters_deprecated?: boolean,
        includeFactoryWithCmkSettings_deprecated?: boolean,
        includeFactoryWithGlobalParameters?: boolean
    ): Promise<Map<Named, Set<Named>>> {
        const modelService = await getServiceInstance<ModelService>(context.contextData, 'ModelService');
        const namedList =
            inputNameds ||
            (await PublisherUtils.getPublishableNameds(context.contextData, {
                includeFactoryWithGlobalParameters_deprecated,
                includeFactoryWithCmkSettings_deprecated,
                includeFactoryWithGlobalParameters
            }));

        await modelService.fetchAll(namedList, true);

        const topologicalMap: Map<Named, Set<Named>> = new Map<Named, Set<Named>>();

        for (const named of namedList) {
            topologicalMap.set(named, undefined);
        }

        for (const named of namedList) {
            // Create dependentSet for each artifact
            const dependentNamedSet = Walker.directConceptDependencies(context, named);

            /* Keep only those dependencies -
             * 1.Not tumbling window self dependencies
             * 2.which are part of the changes
             * remove others so that we can sort them*/
            for (const dependentName of dependentNamedSet) {
                if (
                    (dependentName.name === named.name &&
                        Metamodel.modelOf(named) === Trigger.MODEL &&
                        (named as Trigger).getType() === 'TumblingWindowTrigger') ||
                    !topologicalMap.has(dependentName)
                ) {
                    dependentNamedSet.delete(dependentName);
                }
            }
            topologicalMap.set(named, dependentNamedSet);
        }
        return topologicalMap;
    }

    public static async getResourcesOrdered(
        context: { contextData: IResourceContext; inlineDataset: boolean },
        inputNameds?: Named[],
        includeFactoryWithGlobalParameters_deprecated?: boolean,
        includeFactoryWithCmkSettings_deprecated?: boolean,
        includeFactoryWithGlobalParameters?: boolean
    ): Promise<Named[]> {
        const topologicalMap = await this.getResourcesTopologicalMap(
            context,
            inputNameds,
            includeFactoryWithGlobalParameters_deprecated,
            includeFactoryWithCmkSettings_deprecated,
            includeFactoryWithGlobalParameters
        );

        const orderedNamedList: Named[] = [];

        while (topologicalMap.size > 0) {
            const namedInThisBatch: Named[] = [];

            for (const named of topologicalMap.keys()) {
                // If this named is not referencing any other named then it can be deployed independently
                const dependentNamedSet = topologicalMap.get(named);
                if (dependentNamedSet && dependentNamedSet.size) {
                    // It depends on something, can't deploy it now
                    continue;
                }
                namedInThisBatch.push(named);

                // Remove the reference of named from other named's dependents list.
                topologicalMap.delete(named);
            }

            // This is a safeguard to prevent getting stuck in the infinite loop, potentially due to a bug
            if (namedInThisBatch.length === 0) {
                // At this point we only have circular references in the map, we can take any item and start from there.
                const named = topologicalMap.keys().next().value as Named;
                const path = PublisherUtils.getCircularDependencyPath(topologicalMap, topologicalMap.get(named), [
                    named
                ]);
                throw new Error(format(stringResources.PublishCircularDependenciesError, path));
            }

            // add to ordered list
            orderedNamedList.push(...namedInThisBatch);

            // Remove the reference of named in this batch from other nameds
            for (const named of namedInThisBatch) {
                topologicalMap.forEach((dependentSet) => {
                    dependentSet.delete(named);
                });
            }
        }

        return orderedNamedList;
    }

    public static getResourceType(named: Named): string {
        const concept = Metamodel.modelOf(named).concept;
        if (concept && concept.name === 'trigger') {
            return (named as Trigger).getType();
        }
        return resolveResourceType(Metamodel.modelOf(named).name);
    }

    public static getCircularDependencyPath(
        topologicalMap: Map<Named, Set<Named>>,
        dependencies: Set<Named>,
        path: Named[]
    ): string {
        let resourcesPath = '';

        for (const dependency of dependencies) {
            if (path.some((d) => d === dependency)) {
                path.push(dependency);
                return path
                    .slice(path.indexOf(dependency))
                    .map((p) => p.name)
                    .join(' => ');
            } else {
                path.push(dependency);
                resourcesPath = this.getCircularDependencyPath(topologicalMap, topologicalMap.get(dependency), path);
            }
        }

        return resourcesPath;
    }

    public static async getFactoryCloneForArmTemplate(
        contextData: IResourceContext,
        options?: {
            includeFactoryWithGlobalParameters: boolean;
            includeFactoryWithCmkSettings: boolean;
        }
    ): Promise<Factory> {
        const modelService = await getServiceInstance<ModelService>(contextData, 'ModelService');
        const { includeFactoryWithGlobalParameters, includeFactoryWithCmkSettings } = options || {};
        const factoryClone = Copier.clone(modelService.modelAttributeContext, [modelService.factory])[0] as Factory;
        if (!includeFactoryWithGlobalParameters) {
            factoryClone.parameters.clear();
        } else if (!includeFactoryWithCmkSettings) {
            factoryClone.encryption = undefined;
        }
        if (factoryClone.identity && factoryClone.identity.type) {
            factoryClone.identity = {
                type: factoryClone.identity.type,
                ...(factoryClone.identity.userAssignedIdentities && {
                    userAssignedIdentities: factoryClone.identity.userAssignedIdentities
                })
            };
        }
        return factoryClone;
    }

    public static async createGlobalParametersForArmTemplate(
        contextData: IResourceContext
    ): Promise<GlobalParametersARM> {
        const modelService = await getServiceInstance<ModelService>(contextData, 'ModelService');
        const paramsFromFactory = modelService.factory.parameters;
        const globalParameters = Owned.create<GlobalParametersARM>(Metamodel.modelFor(GlobalParametersARM), undefined);
        globalParameters.name = 'default';
        paramsFromFactory.forEach((param) => {
            globalParameters.parameters.add(param);
        });
        return globalParameters;
    }
}
